import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Sparkles, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const UpgradeSuccess = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      verifyPayment(sessionId);
    } else {
      setVerifying(false);
    }
  }, [searchParams]);

  const verifyPayment = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error("Could not verify payment. Please contact support.");
      setVerifying(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/stripe/checkout-status/${sessionId}`, {
        withCredentials: true
      });

      if (response.data.payment_status === "paid") {
        setVerified(true);
        setVerifying(false);
        toast.success("Payment verified! Welcome to Premium!");
      } else if (response.data.status === "expired") {
        toast.error("Payment session expired");
        setVerifying(false);
      } else {
        // Continue polling
        setTimeout(() => verifyPayment(sessionId, attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error("Error verifying payment:", error);
      setTimeout(() => verifyPayment(sessionId, attempts + 1), pollInterval);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-clarity-bg flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-brand-primary animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-heading font-bold text-white mb-2">Verifying Payment...</h2>
          <p className="text-slate-400">Please wait while we confirm your subscription.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-clarity-bg flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        {verified ? (
          <>
            <div className="w-20 h-20 bg-brand-secondary/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-brand-secondary" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-white mb-4">
              Welcome to Premium!
            </h1>
            <p className="text-lg text-slate-400 mb-8">
              Your subscription is now active. You have unlimited access to the ClarityOS protocol.
            </p>
            <div className="glass rounded-xl p-6 mb-8">
              <div className="flex items-center gap-3 justify-center text-brand-primary">
                <Sparkles className="w-5 h-5" />
                <span className="font-medium">Premium Benefits Unlocked</span>
              </div>
              <ul className="mt-4 space-y-2 text-sm text-slate-400">
                <li>• Unlimited protocol sessions</li>
                <li>• Full progress tracking</li>

              </ul>
            </div>
            <Button
              onClick={() => navigate("/progress")}
              className="bg-brand-primary text-clarity-bg hover:bg-yellow-400 font-bold px-8 py-3"
              data-testid="go-to-dashboard"
            >
              Go to Dashboard
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </>
        ) : (
          <>
            <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-10 h-10 text-amber-400" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-white mb-4">
              Payment Processing
            </h1>
            <p className="text-lg text-slate-400 mb-8">
              Your payment is being processed. If you completed the checkout, your subscription will be activated shortly.
            </p>
            <Button
              onClick={() => navigate("/progress")}
              className="bg-white/10 text-white hover:bg-white/20 font-bold px-8 py-3"
            >
              Return to Dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default UpgradeSuccess;
